import { Box, useTheme } from '@mui/material';
import type { Options } from 'highcharts';
import { type FC, useMemo } from 'react';
import HighchartsReact from '../highcharts';

interface Item {
  source: string;
  target: string;
  value: number;
}

const ChordChart: FC<{ data: Item[]; size?: number | string }> = ({
  data,
  size = '100%',
}) => {
  const theme = useTheme();

  const seriesData: [string, string, number][] = useMemo(() => {
    const dataCollatedBySourceAndTarget = data.reduce((acc, item) => {
      const key = `${item.source}-${item.target}`;
      const existingItem = acc.get(key);
      if (existingItem) {
        existingItem.value += item.value;
      } else {
        acc.set(key, {
          source: item.source,
          target: item.target,
          value: item.value,
        });
      }
      return acc;
    }, new Map<string, { source: string; target: string; value: number }>());

    return Array.from(dataCollatedBySourceAndTarget.values()).map((item) => [
      item.source,
      item.target,
      item.value,
    ]);
  }, [data]);

  const options = {
    title: {
      text: 'Control Flow',
      style: {
        color: theme.palette.text.primary,
        fontSize: '1.5rem',
        fontWeight: 'bold',
      },
    },
    accessibility: {
      point: {
        valueDescriptionFormat:
          '{index}. From {point.from} to ' + '{point.to}: {point.weight}.',
      },
    },
    chart: {
      backgroundColor: 'transparent',
    },
    series: [
      {
        keys: ['from', 'to', 'weight'],
        data: seriesData,
        type: 'dependencywheel',
        name: 'Control',
        tooltip: {
          distance: 50,
        },
        dataLabels: {
          color: theme.palette.text.primary,
          style: {
            textOutline: 'none',
          },
          textPath: {
            // enabled: true,
          },
          distance: 10,
          allowOverlap: true,
        },
        size: '95%',
      },
    ],
  } satisfies Options;

  return (
    <Box
      className="chord-chart"
      sx={{
        maxWidth: size,
        maxHeight: size,
      }}
    >
      <HighchartsReact options={options} />
    </Box>
  );
};

export default ChordChart;
